import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/**
 * TabletServicesNav - Two-column systems-led navigation for tablets
 * 
 * Left column: Growth stages (Acquire, Convert, Retain, Scale)
 * Right column: Services for the selected stage
 * Default: Acquire selected
 */

interface GrowthStage {
  id: string;
  label: string;
  tagline: string;
  services: { label: string; href: string }[];
}

const growthStages: GrowthStage[] = [
  {
    id: 'acquire',
    label: 'Acquire',
    tagline: 'Intent-led customer acquisition',
    services: [
      { label: 'Paid Search', href: '/paid-search' },
      { label: 'Paid Social', href: '/paid-social' },
      { label: 'Programmatic', href: '/programmatic' },
      { label: 'SEO', href: '/seo' },
    ],
  },
  {
    id: 'convert',
    label: 'Convert',
    tagline: 'Conversion and experience optimization',
    services: [
      { label: 'CRO', href: '/cro' },
      { label: 'Landing Pages', href: '/landing-pages' },
      { label: 'Web Development', href: '/web-development' },
      { label: 'UX Strategy', href: '/ux-strategy' },
      { label: 'Creative', href: '/creative' },
    ],
  },
  {
    id: 'retain',
    label: 'Retain',
    tagline: 'Lifecycle and customer value growth',
    services: [
      { label: 'Email Marketing', href: '/email-marketing' },
      { label: 'Lifecycle Marketing', href: '/lifecycle-marketing' },
      { label: 'Loyalty Programs', href: '/loyalty-programs' },
      { label: 'CRM Strategy', href: '/crm-strategy' },
    ],
  },
  {
    id: 'scale',
    label: 'Scale',
    tagline: 'Analytics, infrastructure, and intelligence',
    services: [
      { label: 'Analytics', href: '/analytics' },
      { label: 'Data Management', href: '/data-management' },
      { label: 'AI Automation', href: '/ai-automation' },
      { label: 'Google Cloud Platform', href: '/google-cloud-platform' },
    ],
  },
];

interface TabletServicesNavProps {
  onNavigate: () => void;
}

const TabletServicesNav = ({ onNavigate }: TabletServicesNavProps) => {
  const [selectedStage, setSelectedStage] = useState<string>('acquire');

  const currentStage = growthStages.find(s => s.id === selectedStage) || growthStages[0];

  return (
    <div className="space-y-4">
      {/* Intent Safety Net */}
      <Link
        to="/solutions"
        onClick={onNavigate}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <span>Not sure what you need?</span>
        <span className="text-foreground font-medium">View our growth systems</span>
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>

      <div className="h-px bg-border" />

      {/* Two-Column Layout */}
      <div className="grid grid-cols-2 gap-8">
        {/* Left Column - Growth Stages */}
        <div className="space-y-1">
          {growthStages.map((stage) => {
            const isSelected = selectedStage === stage.id;
            
            return (
              <button
                key={stage.id}
                onClick={() => setSelectedStage(stage.id)}
                className={`
                  w-full text-left py-4 px-3 transition-colors duration-150 border-l-2
                  ${isSelected 
                    ? 'border-primary bg-muted/30' 
                    : 'border-transparent hover:border-muted-foreground/30 hover:bg-muted/10'
                  }
                `}
              >
                <span className={`
                  text-xs uppercase tracking-[0.15em] font-medium block
                  ${isSelected ? 'text-foreground' : 'text-muted-foreground'}
                `}>
                  {stage.label}
                </span>
                <span className="text-sm text-muted-foreground mt-0.5 block">
                  {stage.tagline}
                </span>
              </button>
            );
          })}
        </div>

        {/* Right Column - Services */}
        <div className="py-2">
          <div 
            className="space-y-0 transition-opacity duration-150"
            key={currentStage.id}
          >
            {currentStage.services.map((service) => (
              <Link
                key={service.href}
                to={service.href}
                onClick={onNavigate}
                className="block min-h-[48px] py-3 text-foreground hover:text-primary transition-colors"
              >
                {service.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* View All Services Link */}
      <div className="pt-2 border-t border-border">
        <Link
          to="/services"
          onClick={onNavigate}
          className="flex items-center gap-1 py-3 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
        >
          View all services
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
};

export default TabletServicesNav;
